<template>
    <div id="powder-overview-page">
        <loader
            v-if="loading"
            :message="loaderText"
        />
        <template v-else>
            <li v-if="!powderToOrderData">
                {{ trans('pages.powder_overview.error_fetch') }}
            </li>
            <div
                v-else
                class="leftcolumn"
            >
                <h1>{{ trans('pages.powder_overview.title') }}</h1>
                <p>
                    {{ trans('pages.powder_overview.number_of_colors') }}:
                    {{ powderToOrderData.numberOfColors }}
                </p>
                <p>
                    {{ trans('pages.powder_overview.total_area') }}:
                    {{ powderToOrderData.totalProductArea }} m&#178;
                </p>
                <ui5-button
                    id="navigation-process-PB"
                    @click="calculatePowderToOrder"
                    @mouseover="$refs.tooltipProcessPB.showAt($event.target)"
                    @mouseout="$refs.tooltipProcessPB.close()"
                >
                    {{ trans('pages.powder_overview.process_pb') }}
                </ui5-button>
                <ui5-popover
                    ref="tooltipProcessPB"
                    :allow-target-overlap="false"
                    placement-type="Bottom"
                >
                    <div>
                        {{ trans('pages.powder_overview.process_pb_tooltip') }}
                    </div>
                </ui5-popover>

                <ui5-button
                    id="navigation-export-powder"
                    @click="exportPowderOrder"
                    @mouseover="$refs.tooltipExportPowder.showAt($event.target)"
                    @mouseout="$refs.tooltipExportPowder.close()"
                >
                    {{ trans('pages.powder_overview.export_pdf') }}
                </ui5-button>
                <ui5-popover
                    ref="tooltipExportPowder"
                    :allow-target-overlap="false"
                    placement-type="Bottom"
                >
                    <div>
                        {{ trans('pages.powder_overview.export_pdf_tooltip') }}
                    </div>
                </ui5-popover>

                <ui5-button
                    id="navigation-order-powder"
                    @click="orderPowdersForColor"
                    @mouseover="$refs.tooltipOrderPowder.showAt($event.target)"
                    @mouseout="$refs.tooltipOrderPowder.close()"
                >
                    {{ trans('pages.powder_overview.order_powders') }}
                </ui5-button>
                <ui5-popover
                    ref="tooltipOrderPowder"
                    :allow-target-overlap="false"
                    placement-type="Bottom"
                >
                    <div>
                        {{
                            trans('pages.powder_overview.order_powders_tooltip')
                        }}
                    </div>
                </ui5-popover>

                <br />

                <table
                    cellspacing="0"
                    cellpadding="0"
                    class="product-table"
                >
                    <tr class="product-table-header-tr">
                        <th
                            class="product-table-header-td"
                            align="center"
                        >
                            {{ trans('pages.powder_overview.table.order') }}
                        </th>
                        <th class="product-table-header-td">
                            {{
                                trans(
                                    'pages.powder_overview.table.article_number'
                                )
                            }}
                        </th>
                        <th class="product-table-header-td">
                            {{ trans('pages.powder_overview.table.color') }}
                        </th>
                        <th class="product-table-header-td">
                            {{ trans('pages.powder_overview.table.supplier') }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="center"
                        >
                            {{ trans('pages.powder_overview.table.code') }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="center"
                        >
                            {{ trans('pages.powder_overview.table.producer') }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="center"
                        >
                            {{ trans('pages.powder_overview.table.surface') }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="center"
                        >
                            {{
                                trans(
                                    'pages.powder_overview.table.estimated_usage'
                                )
                            }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="center"
                        >
                            {{ trans('pages.powder_overview.table.in_order') }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="center"
                        >
                            {{
                                trans('pages.powder_overview.table.total_stock')
                            }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="center"
                        >
                            {{
                                trans(
                                    'pages.powder_overview.table.reserved_stock'
                                )
                            }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="center"
                        >
                            {{ trans('pages.powder_overview.table.min_stock') }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="center"
                        >
                            {{ trans('pages.powder_overview.table.shortage') }}
                        </th>
                    </tr>

                    <tbody
                        v-if="powderToOrderData.colors"
                        v-for="color in powderToOrderData.colors"
                        :key="color.colorId"
                    >
                        <tr
                            class="product-table-item-tr"
                            @click="color.showProducts = !color.showProducts"
                        >
                            <td align="center">
                                <ui5-checkbox
                                    :checked="color.selected"
                                    @change="
                                        color.selected = $event.target.checked
                                    "
                                    @click.stop="() => {}"
                                />
                            </td>
                            <td align="center">{{ color.profileCode }}</td>
                            <td align="center">{{ color.ral }}</td>
                            <td align="center">{{ color.supplier }}</td>
                            <td align="center">{{ color.code }}</td>
                            <td align="center">{{ color.producent }}</td>
                            <td align="center">
                                {{
                                    precisionRound(
                                        color.productArea
                                            ? color.productArea
                                            : 0,
                                        2
                                    )
                                }}
                                m²
                            </td>
                            <td align="center">
                                {{
                                    precisionRound(
                                        color.totalPowderToOrder
                                            ? color.totalPowderToOrder
                                            : 0,
                                        2
                                    )
                                }}
                                kg
                            </td>
                            <td align="center">
                                {{
                                    precisionRound(
                                        color.totalPowderOrdered
                                            ? color.totalPowderOrdered
                                            : 0,
                                        2
                                    )
                                }}
                                kg
                            </td>
                            <td align="center">
                                {{
                                    precisionRound(totalStockForColor(color), 2)
                                }}
                            </td>
                            <td align="center">
                                {{ precisionRound(color.reserved, 2) }}
                            </td>
                            <td align="center">
                                {{ precisionRound(color.minStock, 2) }}
                            </td>
                            <td align="center">
                                {{
                                    -1 *
                                    precisionRound(
                                        parseFloat(totalStockForColor(color)) -
                                            parseFloat(color.reserved) +
                                            parseFloat(
                                                color.totalPowderOrdered
                                                    ? color.totalPowderOrdered
                                                    : 0
                                            ),
                                        2
                                    )
                                }}
                            </td>
                        </tr>
                        <template v-if="color.showProducts">
                            <template
                                v-if="color.products && color.products.length"
                            >
                                <tr class="product-table-item-tr-product">
                                    <td
                                        colspan="10"
                                        align="left"
                                    >
                                        <p>
                                            {{
                                                trans(
                                                    'pages.powder_overview.products_in_color'
                                                )
                                            }}
                                        </p>
                                    </td>
                                </tr>
                                <tr
                                    class="product-table-item-tr-product"
                                    v-for="product in color.products"
                                    :key="product.ID"
                                >
                                    <td align="center"></td>
                                    <td
                                        @click="
                                            $router.push(
                                                '/editproduct/?orderId=' +
                                                    product.orderId +
                                                    '&productId=' +
                                                    product.ID +
                                                    '&productState=edit-product'
                                            )
                                        "
                                        align="center"
                                    >
                                        <i class="material-icons">search</i>
                                    </td>
                                    <td align="center">{{ product.ID }}</td>
                                    <td align="center"></td>
                                    <td align="center"></td>
                                    <td align="center"></td>
                                    <td align="center">
                                        {{
                                            precisionRound(
                                                product.productArea
                                                    ? product.productArea
                                                    : 0,
                                                2
                                            )
                                        }}
                                        m²
                                    </td>
                                    <td align="center">
                                        {{
                                            precisionRound(
                                                product.powderToOrder
                                                    ? product.powderToOrder
                                                    : 0,
                                                2
                                            )
                                        }}
                                        kg
                                    </td>
                                    <td align="center">
                                        {{
                                            precisionRound(
                                                product.powderOrdered
                                                    ? product.powderOrdered
                                                    : 0,
                                                2
                                            )
                                        }}
                                        kg
                                    </td>
                                </tr>
                            </template>
                            <tr v-else>
                                <td colspan="9">
                                    <li>
                                        {{
                                            trans(
                                                'pages.powder_overview.no_products'
                                            )
                                        }}
                                    </li>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </template>

        <div
            class="productactions"
            id="productactions"
        >
            <div
                class="productaction"
                @click="goToMenu"
            >
                <p class="productactiontitle">
                    {{ trans('pages.powder_overview.to_overview') }}
                </p>
                <div class="productactionimgdiv">
                    <i class="fa fa-arrow-left fa-3x" />
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import api from '../api/powder.js';
import SpinnerText from '../components/loader/SpinnerText';
import Loader from '../components/loader/Loader';
import { precisionRound } from '../helpers/MathHelper';
import { showInfoMessage } from '../dialogs';
import localization from '../mixins/localization';

export default {
    components: { Loader, SpinnerText },
    mixins: [localization],
    methods: {
        precisionRound,
        loadData() {
            this.loading = true;
            this.loaderText = this.trans(
                'pages.powder_overview.loading_powders'
            );

            api.fetchProductsWherePowderToOrder()
                .then((response) => {
                    const data = response.data.data;
                    if (data.colors) {
                        data.colors.forEach((color) => {
                            color.showProducts = false;
                            color.selected = false;
                        });
                    }
                    this.powderToOrderData = data;

                    this.loading = false;
                })
                .catch(() => {
                    this.powderToOrderData = null;
                    this.loading = false;
                });
        },
        orderPowdersForColor() {
            if (this.orderPowderArray.length == 0) {
                showInfoMessage(
                    this.trans('pages.powder_overview.select_orders')
                );
            } else {
                this.loaderText = this.trans(
                    'pages.powder_overview.ordering_powders'
                );
                this.loading = true;

                api.orderPowderForColors(
                    this.orderPowderArray,
                    this.selectedPrinterPostParameter
                ).then(() => {
                    this.loading = false;
                    this.loadData();
                });
            }
        },
        exportPowderOrder() {
            if (this.orderPowderArray.length == 0) {
                showInfoMessage(
                    this.trans('pages.powder_overview.select_orders')
                );
            } else {
                showInfoMessage(
                    this.trans('pages.powder_overview.export_creating')
                );

                api.exportPowderOrder(
                    this.orderPowderArray,
                    this.selectedPrinterPostParameter
                ).then((response) => {
                    window.open(
                        Config.url +
                            '/resources/tmp/' +
                            response.data.data.filename,
                        '_blank'
                    );
                });
            }
        },
        goToMenu() {
            this.$router.push('/menu');
        },
        calculatePowderToOrder() {
            this.loading = true;
            this.loaderText = this.trans(
                'pages.powder_overview.calculating_powders'
            );

            api.calculatePowderToOrder().then(() => {
                this.loading = false;
                this.loadData();
            });
        },
        totalStockForColor(color) {
            return parseFloat(color.stock) + parseFloat(color.spareStock);
        },
    },
    computed: {
        selectedPrinterPostParameter() {
            return this.$store.getters[
                'printers/getSelectedPrinterPostParameter'
            ]();
        },
        orderPowderArray() {
            if (!this.powderToOrderData || !this.powderToOrderData.colors) {
                return [];
            }
            return this.powderToOrderData.colors
                .filter((x) => x.selected)
                .map((x) => x.colorId);
        },
    },
    data() {
        return {
            loading: false,
            loaderText: '',
            powderToOrderData: null,
        };
    },
    mounted() {
        this.loadData();
    },
};
</script>
